"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Doc, Id } from "@agroproj/backend/convex/_generated/dataModel";
import { FEATURE_FLAG_KEYS } from "@agroproj/backend/convex/constants/featureFlags";
import { type ProducerCompanyFormSchema, type ProducerPersonFormSchema, producerCompanyFormSchema } from "@agroproj/backend/convex/validation/producer";
import { useForm } from "@tanstack/react-form";
import { useAction, useMutation, useQuery } from "convex/react";
import { CheckCircle2Icon, Pencil, PlusIcon, SearchIcon, TriangleAlertIcon, XCircleIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { LoadingPage } from "@/components/loading-page";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { wideFormDialogContentClassName } from "@/components/ui/dialog-content-styles";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useConvexFeatureFlag } from "@/hooks/use-convex-feature-flag";
import { maskHandler, maskRemover } from "@/lib/mask-handler";
import { type CompanyCnpjConflict, type CompanyCnpjField, formatInfoSimplesPrice, getUserFacingErrorMessage, planCnpjCompanyUpdate } from "@/lib/producer-form-behavior";
import { BrazilianValidation } from "@/lib/validation-utils";

/** Contact fields shared by both producer types; they carry over when the type changes. */
type ContactDraft = Pick<ProducerCompanyFormSchema, "phone1" | "phone2" | "email" | "observation">;
const EMPTY_CONTACT: ContactDraft = {
  phone1: "",
  phone2: "",
  email: "",
  observation: ""
};
const EMAIL_PLACEHOLDER = "nome@email.com";

/** Field-level e-mail check from the shared schema, so the message shows under the field. */
function validateEmail({
  value
}: {
  value: string | undefined;
}) {
  const result = producerCompanyFormSchema.shape.email.safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}
export function ProducerFormDialogButton(t0) {
  const $ = _c(12);
  const {
    producerId
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsOpen(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = producerId ? "outline" : "default";
  let t3;
  if ($[1] !== producerId) {
    t3 = producerId ? <><Pencil />Editar</> : <><PlusIcon />Novo produtor</>;
    $[1] = producerId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = <Button data-testid="add-producer-button" size="sm" onClick={t1} variant={t2}>{t3}</Button>;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isOpen || $[7] !== producerId) {
    t5 = isOpen ? <Dialog onOpenChange={setIsOpen} open={isOpen}><DialogContent className={wideFormDialogContentClassName}><DialogHeader><DialogTitle>{producerId ? "Editar produtor" : "Novo produtor"}</DialogTitle><DialogDescription className="sr-only">{producerId ? "Atualize os dados cadastrais do produtor." : "Selecione o tipo de produtor e preencha os dados cadastrais."}</DialogDescription></DialogHeader>{producerId ? <ProducerFormContent onClose={() => setIsOpen(false)} producerId={producerId} /> : <NewProducerForm onClose={() => setIsOpen(false)} />}</DialogContent></Dialog> : null;
    $[6] = isOpen;
    $[7] = producerId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/**
 * Create flow. The type tabs swap only the identity fields; the contact draft is kept here so
 * celular, telefone, e-mail and observações survive switching between the two forms.
 */
function NewProducerForm(t0) {
  const $ = _c(13);
  const {
    onClose
  } = t0;
  const [producerType, setProducerType] = useState("company");
  const [contact, setContact] = useState(EMPTY_CONTACT);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = patch => setContact(current => ({
      ...current,
      ...patch
    }));
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const onContactChange = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = value => {
      if (value === "company" || value === "person") {
        setProducerType(value);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TabsList aria-label="Tipo de produtor" className="w-full"><TabsTrigger data-testid="company-tab-trigger" value="company">Pessoa jurídica</TabsTrigger><TabsTrigger data-testid="person-tab-trigger" value="person">Pessoa física</TabsTrigger></TabsList>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== contact || $[4] !== onClose) {
    t4 = <TabsContent value="company"><ProducerCompanyForm contact={contact} onClose={onClose} onContactChange={onContactChange} /></TabsContent>;
    $[3] = contact;
    $[4] = onClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== contact || $[7] !== onClose) {
    t5 = <TabsContent value="person"><ProducerPersonForm contact={contact} onClose={onClose} onContactChange={onContactChange} /></TabsContent>;
    $[6] = contact;
    $[7] = onClose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== producerType || $[10] !== t4 || $[11] !== t5) {
    t6 = <Tabs className="gap-4" data-testid="producer-type-tabs" onValueChange={t2} value={producerType}>{t3}{t4}{t5}</Tabs>;
    $[9] = producerType;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function ProducerFormContent(t0) {
  const $ = _c(9);
  const {
    producerId,
    onClose
  } = t0;
  let t1;
  if ($[0] !== producerId) {
    t1 = producerId ? {
      producerId
    } : "skip";
    $[0] = producerId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const producer = useQuery(api.producers.queries.getProducer, t1);
  if (!producer) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <LoadingPage />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (producer.documentType === "cpf") {
    let t2;
    if ($[3] !== onClose || $[4] !== producer) {
      t2 = <ProducerPersonForm onClose={onClose} producer={producer} />;
      $[3] = onClose;
      $[4] = producer;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== onClose || $[7] !== producer) {
    t2 = <ProducerCompanyForm onClose={onClose} producer={producer} />;
    $[6] = onClose;
    $[7] = producer;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
interface ProducerFormProps {
  producer?: Doc<"producers">;
  /** Create flow only: contact values carried over from the other producer type. */
  contact?: ContactDraft;
  onContactChange?: (patch: Partial<ContactDraft>) => void;
  onClose: () => void;
}
type CompanyCnpjFeedback = {
  kind: "error";
  message: string;
} | {
  conflicts: CompanyCnpjConflict[];
  kind: "review";
  price?: string;
  registrationStatus?: string;
} | {
  kind: "success";
  message: string;
  price?: string;
  registrationStatus?: string;
};
function ProducerPersonForm({
  producer,
  contact,
  onContactChange,
  onClose
}: ProducerFormProps) {
  const createProducer = useMutation(api.producers.mutations.createProducer);
  const updateProducer = useMutation(api.producers.mutations.updateProducer);
  const [formIsPending, setFormIsPending] = useState(false);
  const enableDocumentValidation = process.env.NEXT_PUBLIC_ENABLE_DOCUMENT_VALIDATION === "true";

  // Each field validates itself, so every blocking error shows under its field. Submitting
  // while a field is already invalid still re-checks every field, instead of doing nothing.
  const form = useForm({
    canSubmitWhenInvalid: true,
    defaultValues: {
      name: producer?.name ?? "",
      documentValue: producer?.documentValue ? maskHandler("CPF", producer.documentValue) : "",
      phone1: producer?.phone1 ? maskHandler("celular", producer.phone1) : contact?.phone1 ?? "",
      phone2: producer?.phone2 ? maskHandler("telefone", producer.phone2) : contact?.phone2 ?? "",
      email: producer?.email ?? contact?.email ?? "",
      observation: producer?.observation ?? contact?.observation ?? ""
    } as ProducerPersonFormSchema,
    onSubmit: async ({
      value: data
    }) => {
      try {
        setFormIsPending(true);
        if (producer) {
          await updateProducer({
            producerId: producer._id,
            name: data.name,
            documentValue: maskRemover(data.documentValue),
            phone1: data.phone1 ? maskRemover(data.phone1) : undefined,
            phone2: data.phone2 ? maskRemover(data.phone2) : undefined,
            email: data.email || undefined,
            observation: data.observation || undefined
          });
          toast.success("Produtor pessoa física atualizado com sucesso");
        } else {
          await createProducer({
            documentType: "cpf" as const,
            name: data.name,
            documentValue: maskRemover(data.documentValue),
            phone1: data.phone1 ? maskRemover(data.phone1) : undefined,
            phone2: data.phone2 ? maskRemover(data.phone2) : undefined,
            email: data.email || undefined,
            observation: data.observation || undefined
          });
          toast.success("Produtor pessoa física criado com sucesso");
        }
        onClose();
      } catch (error) {
        toast.error(getUserFacingErrorMessage(error, `Não foi possível ${producer ? "atualizar" : "criar"} o produtor. Revise os campos e tente novamente.`));
      } finally {
        setFormIsPending(false);
      }
    }
  });
  return <form className="flex w-full flex-col gap-6" data-testid="producer-person-form"
  // Field validators own the messages; native checks would block them with browser text.
  noValidate onSubmit={async e => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  }}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <form.Field name="documentValue" validators={{
          onChange: ({
            value
          }) => {
            if (!value) {
              return "CPF é obrigatório";
            }
            // Skip validation if disabled via environment variable
            if (!enableDocumentValidation) {
              return undefined;
            }
            // Remove mask before validating
            const cleanValue = maskRemover(value);
            const validation = BrazilianValidation.validateCPF(cleanValue);
            if (!validation.isValid) {
              return validation.error;
            }
            return undefined;
          }
        }}>
            {field => <Input autoComplete="off" data-testid="person-cpf" disabled={formIsPending} inputMode="numeric" error={field.state.meta.errors?.[0]} label="CPF" onBlur={field.handleBlur} onChange={e_0 => {
            const value_0 = e_0.target.value;
            field.handleChange(maskHandler("CPF", value_0));
          }} placeholder="000.000.000-00" value={field.state.value} />}
          </form.Field>
        </div>

        <div className="min-w-0">
          <form.Field name="name" validators={{
          onBlur: ({
            value: value_1
          }) => {
            if (!value_1 || value_1.trim().length === 0) {
              return "Nome é obrigatório";
            }
            if (value_1.trim().length < 3) {
              return "Nome deve ter pelo menos 3 caracteres";
            }
            return undefined;
          }
        }}>
            {field_0 => <Input autoComplete="name" data-testid="person-name" disabled={formIsPending} error={field_0.state.meta.errors?.[0]} label="Nome completo" onBlur={field_0.handleBlur} onChange={e_1 => {
            field_0.handleChange(e_1.target.value);
          }} placeholder="Ex: João Silva Santos" value={field_0.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0">
          <form.Field name="phone1">
            {field_1 => <Input autoComplete="tel" data-testid="person-phone1" disabled={formIsPending} inputMode="tel" isOptional label="Celular" onChange={e_2 => {
            const phone1 = maskHandler("celular", e_2.target.value);
            field_1.handleChange(phone1);
            onContactChange?.({
              phone1
            });
          }} type="tel" placeholder="(00) 00000-0000" value={field_1.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0">
          <form.Field name="phone2">
            {field_2 => <Input autoComplete="tel" data-testid="person-phone2" disabled={formIsPending} inputMode="tel" isOptional label="Telefone" onChange={e_3 => {
            const phone2 = maskHandler("telefone", e_3.target.value);
            field_2.handleChange(phone2);
            onContactChange?.({
              phone2
            });
          }} type="tel" placeholder="(00) 0000-0000" value={field_2.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0 sm:col-span-2">
          <form.Field name="email" validators={{
          onBlur: validateEmail
        }}>
            {field_3 => <Input autoComplete="email" data-testid="person-email" disabled={formIsPending} error={field_3.state.meta.errors?.[0]} isOptional label="E-mail" onBlur={field_3.handleBlur} onChange={e_4 => {
            field_3.handleChange(e_4.target.value);
            onContactChange?.({
              email: e_4.target.value
            });
          }} placeholder={EMAIL_PLACEHOLDER} type="email" value={field_3.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0 sm:col-span-2">
          <form.Field name="observation">
            {field_4 => <Textarea data-testid="person-observation" disabled={formIsPending} isOptional label="Observações" onChange={e_5 => {
            field_4.handleChange(e_5.target.value);
            onContactChange?.({
              observation: e_5.target.value
            });
          }} value={field_4.state.value ?? ""} />}
          </form.Field>
        </div>
      </div>

      <DialogFooter className="border-t">
        <Button onClick={e_6 => {
        e_6.preventDefault();
        onClose();
      }} type="button" variant="ghost">
          Cancelar
        </Button>

        <Button disabled={formIsPending} type="submit">
          {formIsPending ? "Salvando..." : producer ? "Salvar" : "Criar"}
        </Button>
      </DialogFooter>
    </form>;
}
function ProducerCompanyForm({
  producer,
  contact,
  onContactChange,
  onClose
}: ProducerFormProps) {
  const lookupCnpj = useAction(api.infoSimples.lookupCnpj);
  const createProducer = useMutation(api.producers.mutations.createProducer);
  const updateProducer = useMutation(api.producers.mutations.updateProducer);
  const [formIsPending, setFormIsPending] = useState(false);
  const [cnpjLookupIsPending, setCnpjLookupIsPending] = useState(false);
  const [cnpjFeedback, setCnpjFeedback] = useState<CompanyCnpjFeedback>();
  const [selectedCnpjFields, setSelectedCnpjFields] = useState<CompanyCnpjField[]>([]);
  const enableDocumentValidation = process.env.NEXT_PUBLIC_ENABLE_DOCUMENT_VALIDATION === "true";
  const {
    enabled: infoSimplesEnabled
  } = useConvexFeatureFlag(FEATURE_FLAG_KEYS.INFOSIMPLES);

  // Each field validates itself, so every blocking error shows under its field. Submitting
  // while a field is already invalid still re-checks every field, instead of doing nothing.
  const form = useForm({
    canSubmitWhenInvalid: true,
    defaultValues: {
      companyName: producer?.companyName ?? "",
      tradingName: producer?.tradingName ?? "",
      documentValue: producer?.documentValue ? maskHandler("CNPJ", producer.documentValue) : "",
      phone1: producer?.phone1 ? maskHandler("celular", producer.phone1) : contact?.phone1 ?? "",
      phone2: producer?.phone2 ? maskHandler("telefone", producer.phone2) : contact?.phone2 ?? "",
      email: producer?.email ?? contact?.email ?? "",
      observation: producer?.observation ?? contact?.observation ?? ""
    } as ProducerCompanyFormSchema,
    onSubmit: async ({
      value: data
    }) => {
      try {
        setFormIsPending(true);
        if (producer) {
          await updateProducer({
            producerId: producer._id,
            companyName: data.companyName,
            tradingName: data.tradingName || undefined,
            documentValue: maskRemover(data.documentValue),
            phone1: data.phone1 ? maskRemover(data.phone1) : undefined,
            phone2: data.phone2 ? maskRemover(data.phone2) : undefined,
            email: data.email || undefined,
            observation: data.observation || undefined
          });
          toast.success("Produtor pessoa jurídica atualizado com sucesso");
        } else {
          await createProducer({
            documentType: "cnpj" as const,
            companyName: data.companyName,
            tradingName: data.tradingName || undefined,
            documentValue: maskRemover(data.documentValue),
            phone1: data.phone1 ? maskRemover(data.phone1) : undefined,
            phone2: data.phone2 ? maskRemover(data.phone2) : undefined,
            email: data.email || undefined,
            observation: data.observation || undefined
          });
          toast.success("Produtor pessoa jurídica criado com sucesso");
        }
        onClose();
      } catch (error) {
        toast.error(getUserFacingErrorMessage(error, `Não foi possível ${producer ? "atualizar" : "criar"} o produtor. Revise os campos e tente novamente.`));
      } finally {
        setFormIsPending(false);
      }
    }
  });
  const applyCnpjPatch = (patch: Partial<Record<CompanyCnpjField, string>>) => {
    if (patch.companyName !== undefined) {
      form.setFieldValue("companyName", patch.companyName);
    }
    if (patch.tradingName !== undefined) {
      form.setFieldValue("tradingName", patch.tradingName);
    }
  };
  const handleCnpjLookup = async () => {
    const cnpj = maskRemover(form.getFieldValue("documentValue"));
    if (cnpj.length !== 14) {
      setCnpjFeedback({
        kind: "error",
        message: "Informe um CNPJ com 14 dígitos."
      });
      return;
    }
    if (enableDocumentValidation && !BrazilianValidation.validateCNPJ(cnpj).isValid) {
      setCnpjFeedback({
        kind: "error",
        message: "Informe um CNPJ válido."
      });
      return;
    }
    try {
      setCnpjLookupIsPending(true);
      setCnpjFeedback(undefined);
      const result = await lookupCnpj({
        cnpj,
        ...(producer ? {
          producerId: producer._id
        } : {})
      });
      const plan = planCnpjCompanyUpdate({
        companyName: form.getFieldValue("companyName"),
        tradingName: form.getFieldValue("tradingName") ?? ""
      }, result.company);
      applyCnpjPatch(plan.safePatch);
      if (plan.conflicts.length > 0) {
        setSelectedCnpjFields(plan.conflicts.map(conflict => conflict.field));
        setCnpjFeedback({
          conflicts: plan.conflicts,
          kind: "review",
          price: formatInfoSimplesPrice(result.integration.price),
          registrationStatus: result.company.status
        });
        return;
      }
      setCnpjFeedback({
        kind: "success",
        message: Object.keys(plan.safePatch).length > 0 ? "Dados oficiais preenchidos. Revise o formulário antes de salvar." : "Os dados informados conferem com o cadastro oficial.",
        price: formatInfoSimplesPrice(result.integration.price),
        registrationStatus: result.company.status
      });
    } catch (error_0) {
      setCnpjFeedback({
        kind: "error",
        message: getUserFacingErrorMessage(error_0, "Não foi possível consultar o CNPJ no momento.")
      });
    } finally {
      setCnpjLookupIsPending(false);
    }
  };
  const finishCnpjReview = (applySelected: boolean) => {
    if (cnpjFeedback?.kind !== "review") return;
    if (applySelected) {
      const patch_0: Partial<Record<CompanyCnpjField, string>> = {};
      for (const conflict_0 of cnpjFeedback.conflicts) {
        if (selectedCnpjFields.includes(conflict_0.field)) {
          patch_0[conflict_0.field] = conflict_0.officialValue;
        }
      }
      applyCnpjPatch(patch_0);
    }
    const appliedCount = applySelected ? selectedCnpjFields.length : 0;
    setCnpjFeedback({
      kind: "success",
      message: appliedCount > 0 ? `${appliedCount} ${appliedCount === 1 ? "campo oficial aplicado" : "campos oficiais aplicados"}. Revise o formulário antes de salvar.` : "Consulta concluída. Os dados atuais foram mantidos.",
      price: cnpjFeedback.price,
      registrationStatus: cnpjFeedback.registrationStatus
    });
    setSelectedCnpjFields([]);
  };
  const resetCnpjFeedback = () => {
    setCnpjFeedback(undefined);
    setSelectedCnpjFields([]);
  };
  const cnpjLookupIsDisabled = (documentValue: string) => {
    const normalizedDocument = maskRemover(documentValue);
    return formIsPending || cnpjLookupIsPending || normalizedDocument.length !== 14 || enableDocumentValidation && !BrazilianValidation.validateCNPJ(normalizedDocument).isValid;
  };
  return <form className="flex w-full flex-col gap-6" data-testid="producer-company-form"
  // Field validators own the messages; native checks would block them with browser text.
  noValidate onSubmit={async e => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  }}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <form.Field name="documentValue" validators={{
              onChange: ({
                value
              }) => {
                if (!value) {
                  return "CNPJ é obrigatório";
                }
                // Skip validation if disabled via environment variable
                if (!enableDocumentValidation) {
                  return undefined;
                }
                // Remove mask before validating
                const cleanValue = maskRemover(value);
                const validation = BrazilianValidation.validateCNPJ(cleanValue);
                if (!validation.isValid) {
                  return validation.error;
                }
                return undefined;
              }
            }}>
                {field => <Input autoComplete="off" data-testid="company-cnpj" disabled={formIsPending || cnpjLookupIsPending} inputMode="numeric" error={field.state.meta.errors?.[0]} label="CNPJ" onBlur={field.handleBlur} onChange={e_0 => {
                const value_0 = e_0.target.value;
                field.handleChange(maskHandler("CNPJ", value_0));
                resetCnpjFeedback();
              }} placeholder="00.000.000/0000-00" value={field.state.value} />}
              </form.Field>
            </div>
            {infoSimplesEnabled ? <form.Subscribe selector={state => [state.values.documentValue]}>
                {([documentValue_0]) => <Button className="w-full sm:w-auto" data-testid="company-cnpj-lookup" disabled={cnpjLookupIsDisabled(documentValue_0)} onClick={() => void handleCnpjLookup()} variant="outline">
                    <SearchIcon aria-hidden="true" />
                    {cnpjLookupIsPending ? "Consultando..." : "Consultar"}
                  </Button>}
              </form.Subscribe> : null}
          </div>
        </div>

        <div className="min-w-0">
          <form.Field name="companyName" validators={{
          onBlur: ({
            value: value_1
          }) => {
            if (!value_1 || value_1.trim().length === 0) {
              return "Razão social é obrigatória";
            }
            if (value_1.trim().length < 3) {
              return "Razão social deve ter pelo menos 3 caracteres";
            }
            return undefined;
          }
        }}>
            {field_0 => <Input autoComplete="organization" data-testid="company-name" disabled={formIsPending} error={field_0.state.meta.errors?.[0]} label="Razão social" onBlur={field_0.handleBlur} onChange={e_1 => {
            field_0.handleChange(e_1.target.value);
          }} placeholder="Ex: Fazenda Santa Maria LTDA" value={field_0.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0 sm:col-span-2">
          <form.Field name="tradingName">
            {field_1 => <Input data-testid="company-trading-name" disabled={formIsPending} label="Nome fantasia" onBlur={field_1.handleBlur} onChange={e_2 => {
            field_1.handleChange(e_2.target.value);
          }} placeholder="Ex: Fazenda Santa Maria" value={field_1.state.value ?? ""} />}
          </form.Field>
        </div>

        {cnpjFeedback?.kind === "success" ? <Alert className="sm:col-span-2" variant="success">
            <CheckCircle2Icon aria-hidden="true" className="size-4" />
            <AlertTitle>
              {cnpjFeedback.registrationStatus ? `Situação cadastral: ${cnpjFeedback.registrationStatus}` : "Consulta concluída"}
            </AlertTitle>
            <AlertDescription>
              {cnpjFeedback.message}
              {cnpjFeedback.price ? ` Custo da consulta: ${cnpjFeedback.price}.` : null}
            </AlertDescription>
          </Alert> : null}

        {cnpjFeedback?.kind === "error" ? <Alert className="sm:col-span-2" variant="destructive">
            <XCircleIcon aria-hidden="true" className="size-4" />
            <AlertTitle>Consulta não concluída</AlertTitle>
            <AlertDescription>{cnpjFeedback.message}</AlertDescription>
          </Alert> : null}

        {cnpjFeedback?.kind === "review" ? <Alert className="sm:col-span-2" data-testid="company-cnpj-review" variant="warning">
            <TriangleAlertIcon aria-hidden="true" className="size-4" />
            <AlertTitle>Revise as divergências encontradas</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <p>
                Selecione os dados oficiais que devem substituir o cadastro atual.
                {cnpjFeedback.price ? ` Custo da consulta: ${cnpjFeedback.price}.` : null}
              </p>

              <div className="border-warning/20 divide-warning/20 divide-y border-y">
                {cnpjFeedback.conflicts.map(conflict_1 => {
              const checked = selectedCnpjFields.includes(conflict_1.field);
              return <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 py-2 sm:grid-cols-[auto_8rem_minmax(0,1fr)]" key={conflict_1.field}>
                      <Checkbox aria-label={`Aplicar ${conflict_1.label}`} checked={checked} onCheckedChange={nextChecked => {
                  setSelectedCnpjFields(current => nextChecked === true ? [...new Set([...current, conflict_1.field])] : current.filter(field_2 => field_2 !== conflict_1.field));
                }} />
                      <div className="text-warning-foreground text-xs/relaxed font-medium">
                        {conflict_1.label}
                      </div>
                      <div className="col-start-2 min-w-0 text-xs/relaxed sm:col-start-3">
                        <div className="text-muted-foreground line-through">
                          {conflict_1.currentValue}
                        </div>
                        <div className="text-success-foreground break-words">
                          {conflict_1.officialValue}
                        </div>
                      </div>
                    </div>;
            })}
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button onClick={() => finishCnpjReview(false)} variant="ghost">
                  Manter cadastro
                </Button>
                <Button disabled={selectedCnpjFields.length === 0} onClick={() => finishCnpjReview(true)} variant="secondary">
                  Aplicar selecionados
                </Button>
              </div>
            </AlertDescription>
          </Alert> : null}

        <div className="min-w-0">
          <form.Field name="phone1">
            {field_3 => <Input autoComplete="tel" data-testid="company-phone1" disabled={formIsPending} inputMode="tel" isOptional label="Celular" onChange={e_3 => {
            const phone1 = maskHandler("celular", e_3.target.value);
            field_3.handleChange(phone1);
            onContactChange?.({
              phone1
            });
          }} type="tel" placeholder="(00) 00000-0000" value={field_3.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0">
          <form.Field name="phone2">
            {field_4 => <Input autoComplete="tel" data-testid="company-phone2" disabled={formIsPending} inputMode="tel" isOptional label="Telefone" onChange={e_4 => {
            const phone2 = maskHandler("telefone", e_4.target.value);
            field_4.handleChange(phone2);
            onContactChange?.({
              phone2
            });
          }} type="tel" placeholder="(00) 0000-0000" value={field_4.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0 sm:col-span-2">
          <form.Field name="email" validators={{
          onBlur: validateEmail
        }}>
            {field_5 => <Input autoComplete="email" data-testid="company-email" disabled={formIsPending} error={field_5.state.meta.errors?.[0]} isOptional label="E-mail" onBlur={field_5.handleBlur} onChange={e_5 => {
            field_5.handleChange(e_5.target.value);
            onContactChange?.({
              email: e_5.target.value
            });
          }} placeholder={EMAIL_PLACEHOLDER} type="email" value={field_5.state.value ?? ""} />}
          </form.Field>
        </div>

        <div className="min-w-0 sm:col-span-2">
          <form.Field name="observation">
            {field_6 => <Textarea data-testid="company-observation" disabled={formIsPending} isOptional label="Observações" onChange={e_6 => {
            field_6.handleChange(e_6.target.value);
            onContactChange?.({
              observation: e_6.target.value
            });
          }} value={field_6.state.value ?? ""} />}
          </form.Field>
        </div>
      </div>

      <DialogFooter className="border-t">
        <Button onClick={e_7 => {
        e_7.preventDefault();
        onClose();
      }} type="button" variant="ghost">
          Cancelar
        </Button>

        <Button disabled={formIsPending} type="submit">
          {formIsPending ? "Salvando..." : producer ? "Salvar" : "Criar"}
        </Button>
      </DialogFooter>
    </form>;
}